// Renderer presets — trimmed to the styles the ITZKATU identity actually uses:
// pixel, dither, ascii, pixel-h-lines (horizontal deform), pixel-v-lines (vertical deform),
// monochrome, loop. Old CRT / RGB-glitch remain as manual options but no longer appear
// in the auto rotation.
import { useEffect, useMemo, useRef, useState } from "react";
import PixelPlaceholder from "./PixelPlaceholder";
import { useSiteContent } from "@/context/SiteContentContext";

// External images usually block canvas access (CORS taint). Route them through
// our backend proxy so every effect can process them; own/relative URLs pass through.
const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
function canvasSafeSrc(src) {
    if (!src || !/^https?:\/\//i.test(src)) return src;
    if (API_ORIGIN && src.startsWith(API_ORIGIN)) return src;
    return `${API_ORIGIN}/api/imgproxy?url=${encodeURIComponent(src)}`;
}

// ---- palette helpers ----
// Read the scheme palette from the closest ancestor's CSS custom properties
// (set by Layout / MediaTile). This lets every effect become a duotone/tritone
// using the exact colors the admin picked — no prop drilling needed.
function hexToRgb(hex) {
    if (!hex || hex[0] !== "#" || hex.length < 7) return { r: 128, g: 128, b: 128 };
    return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
    };
}
function readCssVar(el, name, fallback) {
    if (!el) return fallback;
    const v = getComputedStyle(el).getPropertyValue(name).trim();
    return v || fallback;
}
// Returns the active tritone palette derived from CSS vars (--sc-*).
// `text` = shadows, `accent` = midtones, `bg` = highlights. `accent2` is a
// fourth optional tone we use sparingly (ASCII characters, CRT scanlines).
// Fallback = the `light` scheme (white / purple / black) so effects render
// meaningfully even outside the layout wrapper.
function readPalette(el) {
    return {
        bg:      readCssVar(el, "--sc-bg",      "#FFFFFF"),
        text:    readCssVar(el, "--sc-text",    "#0E0E0E"),
        accent:  readCssVar(el, "--sc-accent",  "#5D3FD3"),
        accent2: readCssVar(el, "--sc-accent2", "#F5D547"),
    };
}
// Perceptual luminance of a hex color (0-255).
function hexLum(hex) {
    const { r, g, b } = hexToRgb(hex);
    return r * 0.299 + g * 0.587 + b * 0.114;
}
// Remap a full RGBA buffer to a 3-tone palette. The palette is normalized so
// the darkest of {text, bg} becomes SHADOWS, the lightest becomes HIGHLIGHTS,
// and `accent` always sits in the middle. That way every scheme — light-bg
// (mustard, cream, white) OR dark-bg (charcoal, black, purple) — produces the
// same duotone/tritone hierarchy the user asked for.
function remapTritone(data, palette) {
    const bgL = hexLum(palette.bg);
    const txL = hexLum(palette.text);
    const shadow = hexToRgb(bgL >= txL ? palette.text : palette.bg);
    const highlight = hexToRgb(bgL >= txL ? palette.bg : palette.text);
    const mid = hexToRgb(palette.accent);
    for (let i = 0; i < data.length; i += 4) {
        const lum = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255;
        let c;
        if (lum < 0.35) c = shadow;
        else if (lum < 0.72) c = mid;
        else c = highlight;
        data[i] = c.r; data[i + 1] = c.g; data[i + 2] = c.b; data[i + 3] = 255;
    }
}

/**
 * MediaRenderer — reusable visual identity layer for itzkatu.
 * Renders the same source image through one of several experimental treatments.
 * The original media stays available inside the project page and the shop — this
 * layer only exists to create curiosity in listing / homepage tiles.
 *
 * Presets:
 *   original      → plain <img>
 *   pixel         → downsample + nearest-neighbor upscale + tritone remap
 *   dither        → ordered dither in canvas using scheme palette (3 tones)
 *   monochrome    → CSS grayscale + contrast + optional tint
 *   crt           → scanline overlay + duotone tint
 *   rgb-glitch    → offset RGB channels + jitter overlay
 *   ascii         → luminance → text characters in canvas
 *   loop          → <video> from render_asset_url (mp4/webm/gif fallback via <img>)
 *
 * Every canvas effect reads the active scheme from the layout via CSS vars
 * (`--sc-bg`, `--sc-text`, `--sc-accent`, `--sc-accent2`) so the visual
 * identity always matches the room.
 */
export default function MediaRenderer({
    src,
    alt = "",
    preset = "original",
    asset_url = "",   // used by "loop" preset (mp4/webm/gif)
    seed = "",        // fallback for placeholder
    className = "",
    dark = false,
    scheme_accent = "", // legacy — kept for callers, palette is read from CSS vars now
}) {
    // GLOBAL switch (admin → home sections): effects_inherit_scheme.
    // ON → canvas effects remap colors to the section/page palette (tritone).
    // OFF → effects keep the ORIGINAL image colors (pixelate/dither only).
    const { content: fxContent } = useSiteContent();
    const inherit_fx = (fxContent || {}).effects_inherit_scheme !== "0";
    if (!inherit_fx) scheme_accent = "";
    // Nothing to render → pixel placeholder (deterministic)
    if (!src && !asset_url) {
        return <PixelPlaceholder seed={seed || alt || "itzkatu"} className={className} />;
    }

    if (preset === "loop" && asset_url) {
        return <LoopRenderer src={asset_url} className={className} alt={alt} />;
    }

    if (!src) {
        return <PixelPlaceholder seed={seed || alt || "itzkatu"} className={className} />;
    }

    if (preset === "original" || !preset) {
        return (
            <img
                src={src}
                alt={alt}
                loading="lazy"
                referrerPolicy="no-referrer"
                className={`w-full h-full object-cover ${className}`}
            />
        );
    }

    if (preset === "monochrome") {
        return (
            <img
                src={src}
                alt={alt}
                loading="lazy"
                referrerPolicy="no-referrer"
                className={`w-full h-full object-cover ${className}`}
                style={{ filter: "grayscale(1) contrast(1.15) brightness(0.95)" }}
            />
        );
    }

    if (preset === "crt-inner") {
        return <CrtRenderer src={src} alt={alt} className={className} dark={dark} inherit_fx={inherit_fx} />;
    }

    if (preset === "crt") {
        return <CrtRenderer src={src} alt={alt} className={className} dark={dark} inherit_fx={inherit_fx} />;
    }

    if (preset === "rgb-glitch") {
        return <RgbGlitchRenderer src={src} alt={alt} className={className} />;
    }

    // Pixel-lines presets — separate renderer, keeps the accent overlay
    if (preset === "pixel-h-lines" || preset === "pixel-v-lines" || preset === "pixel-cross") {
        return (
            <PixelLinesRenderer
                src={src}
                alt={alt}
                preset={preset}
                className={className}
                dark={dark}
                scheme_accent={scheme_accent}
                inherit_fx={inherit_fx}
            />
        );
    }

    // Canvas-based presets (pixel / dither / ascii)
    return (
        <CanvasRenderer
            src={src}
            alt={alt}
            preset={preset}
            className={className}
            dark={dark}
            scheme_accent={scheme_accent}
            inherit_fx={inherit_fx}
        />
    );
}

// --------- Canvas-based renderer for pixel / dither / ascii -----------

function CanvasRenderer({ src, alt, preset, className, dark, scheme_accent = "", inherit_fx = true }) {
    const canvasRef = useRef(null);
    const [status, setStatus] = useState("loading"); // loading | drawn | error

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !src) return undefined;
        const img = new Image();
        // Attempt with CORS first so we can read pixels. If the load fails or
        // getImageData throws, we render the plain image via <img> as fallback
        // (better than a black square).
        img.crossOrigin = "anonymous";
        img.referrerPolicy = "no-referrer";
        img.onload = () => {
            try {
                const w = canvas.clientWidth || 400;
                const h = canvas.clientHeight || 400;
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                if (!ctx) return;

                // Palette derived from the closest ancestor's CSS vars (--sc-*).
                // Every canvas effect turns into a duotone/tritone using EXACTLY
                // the colors the admin picked — no more generic tint.
                const palette = readPalette(canvas);

                if (preset === "pixel") {
                    const px = 48;
                    const aspect = img.height / img.width;
                    const smallW = px;
                    const smallH = Math.max(4, Math.round(px * aspect));
                    const off = document.createElement("canvas");
                    off.width = smallW;
                    off.height = smallH;
                    const octx = off.getContext("2d");
                    octx.drawImage(img, 0, 0, smallW, smallH);
                    // Remap each downsampled pixel to the 3-tone palette
                    // (shadow / accent / highlight — normalized by luminance).
                    const data = octx.getImageData(0, 0, smallW, smallH);
                    if (inherit_fx) remapTritone(data.data, palette);
                    octx.putImageData(data, 0, 0);
                    ctx.imageSmoothingEnabled = false;
                    const scale = Math.max(w / smallW, h / smallH);
                    const dw = smallW * scale;
                    const dh = smallH * scale;
                    // Background = whichever of {bg, text} is lighter (matches
                    // the "highlight" bucket picked by remapTritone).
                    const bgLighter = hexLum(palette.bg) >= hexLum(palette.text) ? palette.bg : palette.text;
                    ctx.fillStyle = inherit_fx ? bgLighter : palette.bg;
                    ctx.fillRect(0, 0, w, h);
                    ctx.drawImage(off, (w - dw) / 2, (h - dh) / 2, dw, dh);
                    setStatus("drawn");
                    return;
                }

                if (preset === "dither") {
                    const px = 96;
                    const aspect = img.height / img.width;
                    const smallW = px;
                    const smallH = Math.max(6, Math.round(px * aspect));
                    const off = document.createElement("canvas");
                    off.width = smallW;
                    off.height = smallH;
                    const octx = off.getContext("2d");
                    octx.drawImage(img, 0, 0, smallW, smallH);
                    // getImageData throws SecurityError for cross-origin — caught below
                    const data = octx.getImageData(0, 0, smallW, smallH);
                    const bayer = [
                        [0, 8, 2, 10],
                        [12, 4, 14, 6],
                        [3, 11, 1, 9],
                        [15, 7, 13, 5],
                    ];
                    // Normalize palette so darkest → shadow, lightest → highlight.
                    // When inherit is OFF → neutral monochrome dither (no palette).
                    const bgLighter = hexLum(palette.bg) >= hexLum(palette.text);
                    const shadow = inherit_fx ? hexToRgb(bgLighter ? palette.text : palette.bg) : hexToRgb("#111111");
                    const highlight = inherit_fx ? hexToRgb(bgLighter ? palette.bg : palette.text) : hexToRgb("#EDEDED");
                    const mid = inherit_fx ? hexToRgb(palette.accent) : hexToRgb("#888888");
                    for (let y = 0; y < smallH; y++) {
                        for (let x = 0; x < smallW; x++) {
                            const i = (y * smallW + x) * 4;
                            const lum = (data.data[i] * 0.299 + data.data[i + 1] * 0.587 + data.data[i + 2] * 0.114) / 255;
                            const t = bayer[y % 4][x % 4] / 16;
                            const v = lum + (t - 0.5) * 0.35;
                            let c;
                            if (v < 0.4) c = shadow;
                            else if (v < 0.7) c = mid;
                            else c = highlight;
                            data.data[i] = c.r;
                            data.data[i + 1] = c.g;
                            data.data[i + 2] = c.b;
                        }
                    }
                    octx.putImageData(data, 0, 0);
                    ctx.imageSmoothingEnabled = false;
                    const scale = Math.max(w / smallW, h / smallH);
                    const dw = smallW * scale;
                    const dh = smallH * scale;
                    ctx.fillStyle = inherit_fx ? (bgLighter ? palette.bg : palette.text) : "#EDEDED";
                    ctx.fillRect(0, 0, w, h);
                    ctx.drawImage(off, (w - dw) / 2, (h - dh) / 2, dw, dh);
                    setStatus("drawn");
                    return;
                }

                if (preset === "ascii") {
                    // Larger cells → more legible, higher contrast
                    const chars = "@#S%?*+;:,. ".split("");
                    const colW = 9;
                    const rowH = 14;
                    const cols = Math.floor(w / colW);
                    const rows = Math.floor(h / rowH);
                    const off = document.createElement("canvas");
                    off.width = cols;
                    off.height = rows;
                    const octx = off.getContext("2d");
                    octx.drawImage(img, 0, 0, cols, rows);
                    const data = octx.getImageData(0, 0, cols, rows);
                    // Background = darker of {bg, text}; characters = accent.
                    // Inherit OFF → neutral: near-black bg + off-white characters.
                    const darker = inherit_fx ? (hexLum(palette.bg) < hexLum(palette.text) ? palette.bg : palette.text) : "#0E0E0E";
                    ctx.fillStyle = darker;
                    ctx.fillRect(0, 0, w, h);
                    ctx.font = "700 14px 'IBM Plex Mono', ui-monospace, monospace";
                    ctx.textBaseline = "top";
                    const accentCol = inherit_fx ? palette.accent : "#EDEDED";
                    for (let y = 0; y < rows; y++) {
                        for (let x = 0; x < cols; x++) {
                            const i = (y * cols + x) * 4;
                            const lum = (data.data[i] * 0.299 + data.data[i + 1] * 0.587 + data.data[i + 2] * 0.114) / 255;
                            const idx = Math.min(chars.length - 1, Math.floor(lum * chars.length));
                            const ch = chars[chars.length - 1 - idx];
                            ctx.fillStyle = accentCol;
                            ctx.globalAlpha = Math.max(0.25, 1 - lum);
                            ctx.fillText(ch, x * colW, y * rowH);
                        }
                    }
                    ctx.globalAlpha = 1;
                    setStatus("drawn");
                    return;
                }
                setStatus("drawn");
            } catch (err) {
                // Cross-origin canvas taint — render plain <img> instead.
                setStatus("error");
            }
        };
        img.onerror = () => {
            // Retry without crossOrigin so at least the plain image loads
            const retry = new Image();
            retry.referrerPolicy = "no-referrer";
            retry.onload = () => setStatus("error"); // triggers plain-img fallback below
            retry.onerror = () => setStatus("error");
            retry.src = src;
        };
        img.src = canvasSafeSrc(src);
        return () => { img.onload = null; img.onerror = null; };
    }, [src, preset, dark, scheme_accent, inherit_fx]);

    return (
        <div className={`relative w-full h-full ${className}`}>
            {status !== "error" && (
                <canvas ref={canvasRef} className="block w-full h-full" />
            )}
            {status === "error" && (
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                    style={{ imageRendering: "pixelated" }}
                />
            )}
            {/* Accent tint — ONLY as fallback when the canvas couldn't process the
                image (CORS taint): keeps some scheme presence on the plain img.
                When the canvas drew, the tritone remap already applied the palette. */}
            {scheme_accent && status === "error" && preset !== "ascii" && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        backgroundColor: scheme_accent,
                        mixBlendMode: "color",
                        opacity: 0.95,
                    }}
                />
            )}
        </div>
    );
}

// --------- CRT overlay renderer — duotone with scanlines -----------

function CrtRenderer({ src, alt, className, dark, inherit_fx = true }) {
    const canvasRef = useRef(null);
    const [status, setStatus] = useState("loading");
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !src) return undefined;
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.referrerPolicy = "no-referrer";
        img.onload = () => {
            try {
                const w = canvas.clientWidth || 400;
                const h = canvas.clientHeight || 400;
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                const palette = readPalette(canvas);
                ctx.fillStyle = palette.bg;
                ctx.fillRect(0, 0, w, h);
                const iw = img.width, ih = img.height;
                const scale = Math.max(w / iw, h / ih);
                const dw = iw * scale, dh = ih * scale;
                ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
                // Remap to duotone (shadows → text, mids → accent, highs → bg)
                if (inherit_fx) {
                    const buf = ctx.getImageData(0, 0, w, h);
                    remapTritone(buf.data, palette);
                    ctx.putImageData(buf, 0, 0);
                }
                setStatus("drawn");
            } catch (err) {
                setStatus("error");
            }
        };
        img.onerror = () => setStatus("error");
        img.src = canvasSafeSrc(src);
        return () => { img.onload = null; img.onerror = null; };
    }, [src, inherit_fx]);

    return (
        <div className={`relative w-full h-full ${className}`}>
            {status !== "error" && <canvas ref={canvasRef} className="block w-full h-full" />}
            {status === "error" && (
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "saturate(1.3) contrast(1.1) brightness(0.9)" }}
                />
            )}
            {/* Scanlines */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none mix-blend-multiply"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(0deg, rgba(0,0,0,0.45) 0px, rgba(0,0,0,0.45) 1px, transparent 1px, transparent 3px)",
                }}
            />
            {/* Vignette */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: "radial-gradient(circle at center, transparent 55%, rgba(0,0,0,0.55) 100%)",
                }}
            />
        </div>
    );
}

// --------- RGB glitch renderer -----------

function RgbGlitchRenderer({ src, alt, className }) {
    return (
        <div className={`relative w-full h-full overflow-hidden bg-black ${className}`}>
            <img
                src={src}
                alt={alt}
                loading="lazy"
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                    filter:
                        "drop-shadow(3px 0 0 rgba(255,0,80,0.85)) drop-shadow(-3px 0 0 rgba(0,220,255,0.85)) contrast(1.05)",
                    mixBlendMode: "screen",
                }}
            />
            <img
                src={src}
                alt=""
                aria-hidden="true"
                loading="lazy"
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: 0.6, mixBlendMode: "multiply" }}
            />
            {/* Horizontal jitter bands */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(0deg, transparent 0px, transparent 6px, rgba(255,255,255,0.06) 6px, rgba(255,255,255,0.06) 7px)",
                }}
            />
        </div>
    );
}

// --------- Pixel-lines renderer (horizontal / vertical / cross bands) -----------
// Deforms the image by chunking it into narrow bands and reprojecting them.
// Reads as "digital pixel lines" per the itzkatu visual language.

function PixelLinesRenderer({ src, alt, preset, className, dark, scheme_accent = "", inherit_fx = true }) {
    const canvasRef = useRef(null);
    const [status, setStatus] = useState("loading");
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !src) return undefined;
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.referrerPolicy = "no-referrer";
        img.onload = () => {
            try {
                const w = canvas.clientWidth || 400;
                const h = canvas.clientHeight || 400;
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.imageSmoothingEnabled = false;
                const palette = readPalette(canvas);
                ctx.fillStyle = palette.bg;
                ctx.fillRect(0, 0, w, h);
                const iw = img.width, ih = img.height;
                const scale = Math.max(w / iw, h / ih);
                const dw = iw * scale;
                const dh = ih * scale;
                const dx = (w - dw) / 2;
                const dy = (h - dh) / 2;
                ctx.drawImage(img, dx, dy, dw, dh);
                // getImageData throws for cross-origin → catch below and keep the plain draw as fallback
                const data = ctx.getImageData(0, 0, w, h);
                const out = ctx.createImageData(w, h);

                if (preset === "pixel-h-lines") {
                    const bandH = 4;
                    for (let y = 0; y < h; y++) {
                        const band = Math.floor(y / bandH);
                        const shift = ((band % 3) - 1) * 4;
                        for (let x = 0; x < w; x++) {
                            const sx = ((x + shift) % w + w) % w;
                            const sy = band * bandH;
                            const si = (sy * w + sx) * 4;
                            const di = (y * w + x) * 4;
                            out.data[di] = data.data[si];
                            out.data[di + 1] = data.data[si + 1];
                            out.data[di + 2] = data.data[si + 2];
                            out.data[di + 3] = 255;
                        }
                    }
                } else if (preset === "pixel-v-lines") {
                    const bandW = 4;
                    for (let x = 0; x < w; x++) {
                        const band = Math.floor(x / bandW);
                        const shift = ((band % 3) - 1) * 4;
                        for (let y = 0; y < h; y++) {
                            const sy = ((y + shift) % h + h) % h;
                            const sx = band * bandW;
                            const si = (sy * w + sx) * 4;
                            const di = (y * w + x) * 4;
                            out.data[di] = data.data[si];
                            out.data[di + 1] = data.data[si + 1];
                            out.data[di + 2] = data.data[si + 2];
                            out.data[di + 3] = 255;
                        }
                    }
                } else {
                    const bandH = 5;
                    const bandW = 5;
                    for (let y = 0; y < h; y++) {
                        for (let x = 0; x < w; x++) {
                            const bh = Math.floor(y / bandH);
                            const bw = Math.floor(x / bandW);
                            const sx = bw * bandW;
                            const sy = bh * bandH;
                            const si = (sy * w + sx) * 4;
                            const di = (y * w + x) * 4;
                            out.data[di] = data.data[si];
                            out.data[di + 1] = data.data[si + 1];
                            out.data[di + 2] = data.data[si + 2];
                            out.data[di + 3] = 255;
                        }
                    }
                }
                ctx.putImageData(out, 0, 0);
                // Tritone remap over the full canvas — every pixel snaps to
                // one of the 3 palette colors (skipped when inherit is OFF).
                if (inherit_fx) {
                    const remap = ctx.getImageData(0, 0, w, h);
                    remapTritone(remap.data, palette);
                    ctx.putImageData(remap, 0, 0);
                }
                setStatus("drawn");
            } catch (err) {
                // CORS taint — fall back to plain image
                setStatus("error");
            }
        };
        img.onerror = () => setStatus("error");
        img.src = canvasSafeSrc(src);
        return () => { img.onload = null; img.onerror = null; };
    }, [src, preset, dark, scheme_accent, inherit_fx]);
    return (
        <div className={`relative w-full h-full ${className}`}>
            {status !== "error" && <canvas ref={canvasRef} className="block w-full h-full" />}
            {status === "error" && (
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                    style={{ imageRendering: "pixelated", filter: preset === "pixel-h-lines" ? "contrast(1.1)" : undefined }}
                />
            )}
        </div>
    );
}

// --------- Animated loop renderer -----------

function LoopRenderer({ src, className, alt }) {
    // .gif → <img>, everything else → <video autoplay muted loop playsInline>
    const isGif = /\.gif(\?|$)/i.test(src);
    if (isGif) {
        return (
            <img
                src={src}
                alt={alt}
                loading="lazy"
                referrerPolicy="no-referrer"
                className={`w-full h-full object-cover ${className}`}
            />
        );
    }
    return (
        <video
            src={src}
            autoPlay
            muted
            loop
            playsInline
            className={`w-full h-full object-cover ${className}`}
        />
    );
}

// Preset list — the actual ITZKATU visual language.
// Removed: monochrome, rgb-glitch (user removed these from the palette).
export const RENDER_PRESETS = [
    { value: "", label: "random (rotates through presets)" },
    { value: "original", label: "original (photo, no effect)" },
    { value: "pixel", label: "pixel display" },
    { value: "pixel-h-lines", label: "pixel — horizontal lines" },
    { value: "pixel-v-lines", label: "pixel — vertical lines" },
    { value: "pixel-cross", label: "pixel — cross bands" },
    { value: "dither", label: "dithered / noise" },
    { value: "ascii", label: "ASCII render" },
    { value: "crt", label: "CRT / scanlines" },
    { value: "loop", label: "animated loop (upload)" },
];

// Deterministic preset picker — rotates through the trimmed itzkatu identity.
export function derivePreset(seed = "", { fallback = "pixel" } = {}) {
    if (!seed) return fallback;
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const rotation = ["pixel", "pixel-h-lines", "pixel-v-lines", "dither", "ascii", "pixel-cross", "crt"];
    return rotation[h % rotation.length] || fallback;
}
